import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Bell, BellRing, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Follow/Following toggle for an organizer. Signed-out users are sent to
 * /login. Shows live follower count when showCount is set.
 */
export const FollowOrganizerButton = ({ slug, showCount = false, size = 'sm', className = '' }) => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [following, setFollowing] = useState(false);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/organizations/public/${slug}/follow-status`, token ? auth() : undefined)
      .then(res => { setFollowing(!!res.data.following); setCount(res.data.followers_count ?? 0); })
      .catch(() => {});
  }, [slug, token, auth]);

  const toggle = async () => {
    if (!isAuthenticated) {
      toast.info('Sign in to follow organizers and get new-event alerts');
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      const res = following
        ? await axios.delete(`${API}/organizations/public/${slug}/follow`, auth())
        : await axios.post(`${API}/organizations/public/${slug}/follow`, {}, auth());
      setFollowing(!!res.data.following);
      setCount(res.data.followers_count ?? count);
      toast.success(res.data.following
        ? "Following — we'll ping you when they announce a new event"
        : 'Unfollowed');
    } catch {
      toast.error('Could not update follow — try again');
    }
    setBusy(false);
  };

  if (!slug) return null;

  return (
    <Button
      size={size}
      variant={following ? 'secondary' : 'default'}
      onClick={toggle}
      disabled={busy}
      className={`${following
        ? 'border border-fuchsia-500/40 text-fuchsia-400'
        : 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white hover:text-white shadow-md shadow-fuchsia-600/25'} ${className}`}
      data-testid="follow-organizer-btn"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
      ) : following ? (
        <BellRing className="h-3.5 w-3.5 mr-1" />
      ) : (
        <Bell className="h-3.5 w-3.5 mr-1" />
      )}
      {following ? 'Following' : 'Follow'}
      {showCount && count != null && count > 0 && (
        <span className="ml-1.5 text-[11px] opacity-70" data-testid="followers-count">{count}</span>
      )}
    </Button>
  );
};

export default FollowOrganizerButton;
